/**
 * Resolve API + iframe URLs from the environment label. Partners
 * normally just pass `environment: 'production'` and the SDK picks
 * the right hosts. Power users can override with explicit `apiBaseUrl`
 * / `iframeUrl` for local dev or self-hosted setups.
 */

export type Environment = 'production' | 'sandbox' | 'development';

export interface ResolvedHosts {
   apiBaseUrl: string;
   iframeUrl: string;
}

// `apiBaseUrl` is the API ORIGIN only — endpoint paths carry the `/v1`
// version segment (e.g. bootstrap.ts calls `${apiBaseUrl}/v1/embed/bootstrap`).
// Do NOT include `/v1` here or it doubles. Matches the Node SDK convention
// (baseUrl = origin, resource paths = `/v1/...`).
const HOSTS: Record<Environment, ResolvedHosts> = {
   production: {
      apiBaseUrl: 'https://0gate-api.0bit.app',
      iframeUrl: 'https://0gate.0bit.app',
   },
   sandbox: {
      apiBaseUrl: 'https://0gate-api-sandbox.0bit.app',
      iframeUrl: 'https://0gate-sandbox.0bit.app',
   },
   development: {
      apiBaseUrl: 'http://localhost:3000',
      iframeUrl: 'http://localhost:3001',
   },
};

export function resolveHosts(
   env: Environment | undefined,
   overrides: Partial<ResolvedHosts>,
): ResolvedHosts {
   const base = HOSTS[env ?? 'production'];
   return {
      apiBaseUrl: (overrides.apiBaseUrl ?? base.apiBaseUrl).replace(/\/+$/, ''),
      iframeUrl: (overrides.iframeUrl ?? base.iframeUrl).replace(/\/+$/, ''),
   };
}

/** Pull origin from a URL string for postMessage targetOrigin checks. */
export function originOf(url: string): string {
   try {
      return new URL(url).origin;
   } catch {
      throw new Error(`Cannot parse origin from URL: ${url}`);
   }
}
